import DOMPurify from 'dompurify';

let mermaidPromise = null;

/**
 * Lazily load and initialise mermaid (kept out of the main bundle; ~1MB).
 * Shared by the editor node view and the Word/PDF exporters so mermaid is initialised once.
 * @returns {Promise<object>} The mermaid default export.
 */
export const loadMermaid = () => {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid')
      .then((module) => {
        const mermaid = module.default || module;
        mermaid.initialize({
          startOnLoad: false,
          theme: 'default',
          // Diagram source comes from shared document HTML and AI edit plans, so it is untrusted:
          // 'strict' sanitizes labels, blocks javascript: links and disables click callbacks.
          securityLevel: 'strict',
          fontFamily: 'Arial, Helvetica, sans-serif',
        });
        return mermaid;
      })
      .catch((error) => {
        mermaidPromise = null;
        throw error;
      });
  }
  return mermaidPromise;
};

/**
 * Sanitize rendered mermaid SVG before it is injected into the DOM.
 * Defence in depth on top of securityLevel 'strict': keeps SVG, SVG filters and the HTML
 * labels mermaid places inside <foreignObject>, and strips scripts, event handlers and
 * javascript:/data: links.
 *
 * @param {string} svg - SVG markup from mermaid.render.
 * @returns {string} Sanitized SVG markup.
 */
export const sanitizeMermaidSvg = (svg) => {
  if (!svg || typeof svg !== 'string') return '';
  return DOMPurify.sanitize(svg, {
    USE_PROFILES: { svg: true, svgFilters: true, html: true },
    ADD_TAGS: ['foreignObject'],
    ADD_ATTR: ['dominant-baseline'],
    HTML_INTEGRATION_POINTS: { foreignobject: true },
  });
};

/**
 * Decode common HTML entities that may appear in `data-mermaid-code` attributes.
 *
 * @param {string} raw - Raw attribute value or textContent.
 * @returns {string} Decoded mermaid source code.
 */
export const decodeMermaidCode = (raw) =>
  raw
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&amp;/g, '&')
    .replace(/&#10;/g, '\n');

/**
 * Replace every `<foreignObject>` in an SVG DOM tree with an SVG `<text>`
 * element that carries the same visible text. This removes the embedded HTML
 * content that causes Chrome to mark the canvas as "tainted" (blocking
 * `toBlob` / `toDataURL`), while keeping the label text visible in the
 * rasterised output.
 *
 * @param {SVGElement} svgEl - Root `<svg>` element (mutated in place).
 */
const replaceForeignObjectsWithText = (svgEl) => {
  const ns = 'http://www.w3.org/2000/svg';
  svgEl.querySelectorAll('foreignObject').forEach((fo) => {
    const text = (fo.textContent || '').trim();
    if (!text) { fo.remove(); return; }

    const x = parseFloat(fo.getAttribute('x')) || 0;
    const y = parseFloat(fo.getAttribute('y')) || 0;
    const w = parseFloat(fo.getAttribute('width')) || 100;
    const h = parseFloat(fo.getAttribute('height')) || 30;

    const textEl = svgEl.ownerDocument.createElementNS(ns, 'text');
    textEl.setAttribute('x', String(x + w / 2));
    textEl.setAttribute('y', String(y + h / 2));
    textEl.setAttribute('dominant-baseline', 'central');
    textEl.setAttribute('text-anchor', 'middle');
    textEl.setAttribute('font-family', 'Arial, Helvetica, sans-serif');
    textEl.setAttribute('font-size', '14');
    textEl.setAttribute('fill', '#333');
    textEl.textContent = text;

    fo.parentNode.replaceChild(textEl, fo);
  });
};

/**
 * Render mermaid source code to an SVG string.
 *
 * @param {string} code - Mermaid diagram source.
 * @returns {Promise<string>} Rendered SVG markup.
 */
export const renderMermaidToSvg = async (code) => {
  const mermaid = await loadMermaid();
  const id = `mermaid-export-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  try {
    const { svg } = await mermaid.render(id, code.trim());
    return sanitizeMermaidSvg(svg);
  } finally {
    // Clean up orphan container mermaid may leave in the DOM
    const orphan = document.getElementById(`d${id}`);
    if (orphan) orphan.remove();
  }
};

/**
 * Convert an SVG string to a PNG as a `Uint8Array`.
 *
 * Replaces `<foreignObject>` elements with SVG `<text>` so the canvas is
 * never tainted by embedded HTML, then draws the SVG onto a 2x-scaled
 * offscreen canvas and exports to PNG.
 *
 * @param {string} svgString - Raw SVG markup (e.g. from mermaid).
 * @param {Object} [opts]
 * @param {number} [opts.maxWidth=600] - Maximum pixel width for the output.
 * @param {number} [opts.scale=2] - Pixel density multiplier.
 * @returns {Promise<{buffer: Uint8Array, width: number, height: number}|null>}
 */
export const svgToPngBuffer = (svgString, { maxWidth = 600, scale = 2 } = {}) =>
  new Promise((resolve) => {
    try {
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
      const svgEl = svgDoc.documentElement;

      replaceForeignObjectsWithText(svgEl);

      let width = parseFloat(svgEl.getAttribute('width')) || 600;
      let height = parseFloat(svgEl.getAttribute('height')) || 400;

      const viewBox = svgEl.getAttribute('viewBox');
      if (viewBox) {
        const parts = viewBox.split(/[\s,]+/).map(Number);
        if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
          const vbRatio = parts[2] / parts[3];
          if (!svgEl.getAttribute('width') || !svgEl.getAttribute('height')) {
            width = Math.min(parts[2], 800);
            height = width / vbRatio;
          }
        }
      }

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      svgEl.setAttribute('width', String(width));
      svgEl.setAttribute('height', String(height));

      const canvas = document.createElement('canvas');
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);

      const serializer = new XMLSerializer();
      const svgData = serializer.serializeToString(svgEl);
      const url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgData);

      const img = new window.Image();

      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((pngBlob) => {
          if (!pngBlob) { resolve(null); return; }
          pngBlob.arrayBuffer().then((ab) => {
            resolve({
              buffer: new Uint8Array(ab),
              width: Math.round(width),
              height: Math.round(height),
            });
          });
        }, 'image/png');
      };

      img.onerror = () => resolve(null);
      img.src = url;
    } catch {
      resolve(null);
    }
  });

/**
 * Render a mermaid code string to a PNG buffer, ready for embedding in a
 * Word document. Combines {@link renderMermaidToSvg} and {@link svgToPngBuffer}.
 *
 * @param {string} code - Mermaid diagram source (decoded, not entity-encoded).
 * @returns {Promise<{buffer: Uint8Array, width: number, height: number}|null>}
 */
export const renderMermaidToPng = async (code) => {
  const svg = await renderMermaidToSvg(code);
  return svgToPngBuffer(svg);
};
